// LessonText.tsx — an article/reading step. Editorial, unhurried, legible.
import { useState, type CSSProperties } from 'react';
import { Display, Body, Pressable, Btn } from '@/components/ui';
import { Markdown } from '@/components/Markdown';
import { Icon } from '@/components/icons';
import { CompletionButton } from '@/components/CompletionButton';
import { MeditationNote, isMeditation } from '@/components/MeditationNote';
import { FavouriteButton } from '@/components/FavouriteButton';
import { ImageLightbox } from '@/components/ImageLightbox';
import { LessonAudioPlayer } from '@/components/LessonAudioPlayer';
import { useProgress } from '@/hooks/useProgress';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { openExternal } from '@/services/openExternal';
import { SHOW_IAP } from '@/config/supabase';
import { FREE_CAP_STEP_ID } from '@/services/contentProvider';
import { openPaywall } from '@/services/paywallStore';
import type { Step } from '@/types';

const NATIVE_SIGNUP_MD = [
  '## The end of your free trial',
  'This is where the free part of the renurture programme ends. To carry on, you will need to become a member.',
  'Membership unlocks the rest of the lessons, videos and tools, and more besides, including live webinars with our founder, Dr Craig Newman. These are held outside the app, and once you are a member we will email you the details of how to join.',
  'You can join right here in the app, then carry on where you left off.',
  'Whatever you decide, the reflections you have written always stay yours to return to.',
].join('\n\n');

// Lesson images are drawn for a white background, so they sit on a full-width
// white band (the reading body stays on cream). `full` fills the band edge to
// edge; otherwise the image is centred at ~60%. Tapping opens a zoomable viewer
// so infographics with baked-in text stay legible.
function ImageBand({
  src,
  full,
  mt = 0,
  mb = 0,
  onZoom,
  disableZoom,
}: {
  src: string;
  full?: boolean;
  mt?: number;
  mb?: number;
  onZoom: (src: string) => void;
  /** Self-compassion course: images are never enlarged, so render the picture
   *  plain (no tap target, no expand badge, no lightbox). */
  disableZoom?: boolean;
}) {
  const isDesktop = useIsDesktop();
  const imgStyle: CSSProperties = { display: 'block', width: '100%', maxHeight: full ? '52vh' : 240, margin: 0, objectFit: 'contain' };
  const inner = (
    <div
      style={{
        display: 'block',
        position: 'relative',
        width: full ? (isDesktop ? 460 : '100%') : '60%',
        margin: full && !isDesktop ? 0 : '0 auto',
      }}
    >
      <img src={src} alt="" aria-hidden="true" decoding="async" style={imgStyle} />
    </div>
  );
  return (
    <div
      style={{
        background: '#fff',
        width: 'calc(100% + 52px)',
        marginLeft: -26,
        marginRight: -26,
        marginTop: mt,
        marginBottom: mb,
        padding: full ? '14px 0' : '16px 26px',
      }}
    >
      {disableZoom ? (
        inner
      ) : (
        <Pressable
          as="button"
          onClick={() => onZoom(src)}
          ariaLabel="Enlarge image"
          style={{
            display: 'block',
            position: 'relative',
            width: full ? (isDesktop ? 460 : '100%') : '60%',
            margin: full && !isDesktop ? 0 : '0 auto',
          }}
        >
          <img src={src} alt="" aria-hidden="true" decoding="async" style={imgStyle} />
          {/* quiet affordance that the image opens larger */}
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              right: 8,
              bottom: 8,
              width: 30,
              height: 30,
              borderRadius: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(20,14,8,0.46)',
              color: '#FBF1DE',
            }}
          >
            <Icon name="expand" size={15} />
          </span>
        </Pressable>
      )}
    </div>
  );
}

export function LessonText({
  step,
  isComplete,
  onComplete,
  onClose,
  disableImageZoom = false,
}: {
  step: Step;
  isComplete: boolean;
  onComplete: () => void;
  onClose: () => void;
  /** Self-compassion course: never offer image enlarge (no lightbox) — it's never
   *  needed there and the full-screen viewer trapped users on iPad. */
  disableImageZoom?: boolean;
}) {
  // Text can arrive either as `body` (paragraph array) or `body_md` (markdown —
  // used across the self-compassion course). Counting only `body` misclassified
  // every body_md lesson as "image-only", hiding the copy and showing the image
  // as standalone content ("2 images / no text"). Count both.
  // The Sign-Up step's lesson text (and its narration) tell people to join on the
  // website. On a native build that can sell in-app, show this instead. Kept in code,
  // not the .md, because the lesson content is regenerated from the xlsx.
  const nativeSignUp = SHOW_IAP && step.id === FREE_CAP_STEP_ID;
  const hasText = nativeSignUp || !!((step.body && step.body.length) || step.body_md);
  // An image with no text stands alone (full width, centred); with text it sits
  // above the copy as a ~60%-width header.
  const imageOnly = !!step.image_url && !hasText;
  const body = step.body ?? ['This short piece is part of your journey here. Take it slowly.'];
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  const progress = useProgress();
  const fontScale = progress.state.fontScale;
  const sz = (px: number) => Math.round(px * fontScale * 10) / 10;
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 82,
        background: 'var(--cream)',
        display: 'flex',
        flexDirection: 'column',
        animation: 'rise .42s cubic-bezier(.2,.7,.3,1)',
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 260,
            background:
              'radial-gradient(120% 80% at 50% -10%, rgba(255,250,240,0.95), rgba(246,224,190,0.16) 44%, transparent 64%)',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            padding: 'calc(18px + var(--safe-top)) 18px 0',
            position: 'relative',
            zIndex: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Pressable
            as="button"
            onClick={onClose}
            ariaLabel="Close"
            style={{
              width: 44,
              height: 44,
              borderRadius: 999,
              border: '1px solid var(--hair)',
              background: 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
              color: 'var(--ink)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'var(--edge), var(--sh1)',
            }}
          >
            <Icon name="chevLeft" size={20} />
          </Pressable>
          <FavouriteButton stepId={step.id} />
        </div>

        <div style={{ padding: '16px 26px calc(46px + var(--safe-bottom))', position: 'relative' }}>
          {/* image-only lessons skip the Reading eyebrow + title — the image is
              the whole content; a header above it just looks odd. */}
          {!imageOnly && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                whiteSpace: 'nowrap',
                color: step.audio_intro ? 'var(--green-deep)' : 'var(--amber-deep)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.04em',
                marginBottom: 15,
              }}
            >
              {step.audio_intro ? (
                <>
                  <Icon name="voice" size={16} /> A few words from Dr Craig Newman
                </>
              ) : (
                <>
                  <Icon name="book" size={16} /> Reading{step.read_minutes ? ` · ${step.read_minutes} min` : ''}
                </>
              )}
            </div>
          )}
          {/* Text-size selector — on text lessons too (incl. the self-compassion
              course), not just video lessons. Hidden on image-only lessons. */}
          {!imageOnly && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: 3, borderRadius: 999, border: '1px solid var(--hair)', background: 'var(--card-top)' }}>
                <button type="button" onClick={() => progress.setFontScale(fontScale - 0.1)} aria-label="Smaller text" style={{ width: 28, height: 28, borderRadius: 999, border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 13, cursor: 'pointer' }}>A</button>
                <button type="button" onClick={() => progress.setFontScale(1)} aria-label="Reset text size" style={{ minWidth: 34, padding: '0 8px', height: 28, borderRadius: 999, border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 10, fontWeight: 600, cursor: 'pointer' }}>{Math.round(fontScale * 100)}%</button>
                <button type="button" onClick={() => progress.setFontScale(fontScale + 0.1)} aria-label="Larger text" style={{ width: 28, height: 28, borderRadius: 999, border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 18, cursor: 'pointer' }}>A</button>
              </div>
            </div>
          )}
          {/* self-compassion course header image: 80% width, centred, on cream.
              maxHeight caps it so a tall portrait never fills the screen (iPad) and
              the Complete button stays reachable. In the SC course it's never
              tappable-to-enlarge (disableImageZoom). */}
          {step.image_url && step.image_layout === 'inline' && (
            disableImageZoom ? (
              <div style={{ display: 'block', width: '80%', margin: '6px auto 22px' }}>
                <img
                  src={step.image_url}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  style={{ display: 'block', width: '100%', maxHeight: '46vh', borderRadius: 16, objectFit: 'cover', boxShadow: 'var(--sh1)' }}
                />
              </div>
            ) : (
              <Pressable
                as="button"
                onClick={() => setZoomSrc(step.image_url as string)}
                ariaLabel="Enlarge image"
                style={{ display: 'block', width: '80%', margin: '6px auto 22px' }}
              >
                <img
                  src={step.image_url}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  style={{ display: 'block', width: '100%', maxHeight: '46vh', borderRadius: 16, objectFit: 'cover', boxShadow: 'var(--sh1)' }}
                />
              </Pressable>
            )
          )}
          {step.image_url && hasText && step.image_layout === 'above' && (
            <ImageBand src={step.image_url} full mt={8} mb={20} onZoom={setZoomSrc} disableZoom={disableImageZoom} />
          )}
          {step.image_url && hasText && !step.image_layout && (
            <ImageBand src={step.image_url} mt={8} mb={20} onZoom={setZoomSrc} disableZoom={disableImageZoom} />
          )}
          {/* AI-narrated audio version — sits just below the header image. */}
          {step.audio_url && !nativeSignUp && <LessonAudioPlayer src={step.audio_url} />}
          {!imageOnly && <Display parts={step.title} size={29} lh={1.14} />}
          {step.lede && (
            <p
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontWeight: 300,
                fontStyle: 'italic',
                fontSize: sz(19),
                lineHeight: 1.5,
                color: 'var(--amber-deep)',
                margin: '18px 0 4px',
              }}
            >
              {step.lede}
            </p>
          )}
          {/* My-voice (transcribed) audio: the spoken piece is the content, the
              transcript below is for reference — so the Complete button sits at the
              TOP, like the video player, reachable without scrolling the transcript. */}
          {step.audio_intro && !imageOnly && (
            <div style={{ marginTop: 22 }}>
              <CompletionButton
                variant="soft"
                done={isComplete}
                onComplete={onComplete}
                onDone={onClose}
                label="Mark as done"
                doneLabel="Done · tap to close"
              />
            </div>
          )}
          {step.audio_intro && !imageOnly && (
            <div style={{ marginTop: 24, marginBottom: -4, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--ink-soft)', opacity: 0.7 }}>
              Transcript
            </div>
          )}
          {imageOnly ? (
            // content lives entirely in the image → full white band, edge to edge.
            <ImageBand src={step.image_url as string} full mt={16} mb={8} onZoom={setZoomSrc} disableZoom={disableImageZoom} />
          ) : nativeSignUp ? (
            <div style={{ marginTop: 16 }}>
              <Markdown source={NATIVE_SIGNUP_MD} scale={fontScale} />
              <div style={{ marginTop: 20 }}>
                <Btn full size="lg" onClick={openPaywall}>See membership options</Btn>
              </div>
            </div>
          ) : step.body_md ? (
            <div style={{ marginTop: 16 }}>
              <Markdown source={step.body_md} scale={fontScale} />
            </div>
          ) : (
            <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 19 }}>
              {body.map((p, i) => (
                <Body key={i} size={sz(16)} style={{ lineHeight: 1.66 }}>
                  {p}
                </Body>
              ))}
            </div>
          )}
          {step.image_url && hasText && step.image_layout === 'below' && (
            <ImageBand src={step.image_url} full mt={24} mb={4} onZoom={setZoomSrc} disableZoom={disableImageZoom} />
          )}
          {step.link_url && (
            <Pressable
              as="button"
              onClick={() => openExternal(step.link_url as string)}
              ariaLabel={step.link_label ?? 'Open link'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, width: '100%',
                marginTop: 24, padding: '15px 18px', borderRadius: 14,
                border: '1.5px solid rgba(29,158,117,0.42)',
                background: 'linear-gradient(180deg, rgba(29,158,117,0.11), rgba(29,158,117,0.045))',
                color: 'var(--green-deep)', cursor: 'pointer',
                fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, fontWeight: 700,
              }}
            >
              <Icon name="external" size={17} /> {step.link_label ?? 'Open'}
            </Pressable>
          )}
          {isMeditation(step.title) && <MeditationNote />}

          {/* Reading lessons keep the Complete button at the foot (read, then mark
              read). Audio-intro lessons already have it at the top, so skip here. */}
          {!step.audio_intro && (
            <div style={{ marginTop: 26 }}>
              <CompletionButton
                variant="soft"
                done={isComplete}
                onComplete={onComplete}
                onDone={onClose}
                label="Mark as read"
                doneLabel="Done · tap to close"
              />
            </div>
          )}
        </div>
      </div>
      {zoomSrc && <ImageLightbox src={zoomSrc} onClose={() => setZoomSrc(null)} />}
    </div>
  );
}
